import * as React from 'react';
import * as moment from 'moment';
import { DatePicker } from 'antd';
import languages from '../i18n/utils/languages';
import * as intl from 'react-intl-universal';
const { RangePicker } = DatePicker;

interface iProps {
    defaultDate: any;
    disabled: boolean;
    lang?: string;
    onDateChange?: (value: any) => any;
    showTime?: boolean;
    disabledDate?: 'withToday' | 'primary'; // withToday 禁用今天及今天之后的日期 primary 禁用今天之后的日期
    disabledRanges?: Array<string>; // 禁用日期范围按钮生成器
    extraRanges?: { key: string; text: string; value: any }[]; // 追加的自定义快捷按钮(如"近一个月")
    bodyStyle?: { [key: string]: any }; // 标题样式
    dropdownClassName?: string; // 额外的弹出日历 className
    placeholder?: [string, string];
    forbidClear?: boolean;
    disabledDateRange?: any;
    onCalendarChange?: Function;
}

export default class Picker extends React.Component<iProps> {
    state: {
        currentIndex: number;
        lastIndex: number;
        currentIndex2: number;
        open: boolean;
    } = {
        currentIndex: -1,
        lastIndex: 1,
        currentIndex2: -1,
        open: false,
    };

    // 默认范围
    private get ranges() {
        const { disabledRanges = [], extraRanges = [] } = this.props;
        const { currentIndex, lastIndex } = this.state;
        const defaultRanges = [
            {
                key: 'd',
                text: intl.get('Report.ToolsToday'),
                value: [moment('00:00:00', 'HH:mm:ss'), moment('23:59:59', 'HH:mm:ss')],
            },
            {
                key: 'd-1',
                text: intl.get('Report.ToolsYesterday'),
                value: [
                    moment('00:00:00', 'HH:mm:ss').subtract(currentIndex === 1 ? lastIndex : 1, 'd'),
                    moment('23:59:59', 'HH:mm:ss').subtract(currentIndex === 1 ? lastIndex : 1, 'd'),
                ],
            },
            {
                key: 'm',
                text: intl.get('Report.ToolsThisMonth'),
                value: [moment('00:00:00', 'HH:mm:ss').startOf('month'), moment('23:59:59', 'HH:mm:ss').endOf('month')],
            },
            {
                key: 'm-1',
                text: intl.get('Report.ToolsLastMonth'),
                value: [
                    moment('00:00:00', 'HH:mm:ss')
                        .subtract(currentIndex === 3 ? lastIndex : 1, 'months')
                        .startOf('month'),
                    moment()
                        .subtract(currentIndex === 3 ? lastIndex : 1, 'months')
                        .endOf('month'),
                ],
                // }, {
                //     key: 'd-7',
                //     text: intl.get('Report.ToolsLastSevenDay'),
                //     value: [moment('00:00:00', 'HH:mm:ss').subtract(6, 'd'), moment('23:59:59', 'HH:mm:ss')]
                // }, {
                //     key: 'm-30d',
                //     text: intl.get('Report.ToolsLastThirtyDay'),
                //     value: [moment('00:00:00', 'HH:mm:ss').subtract(29, 'd'), moment('23:59:59', 'HH:mm:ss')]
            },
            {
                key: 'y',
                text: intl.get('Report.ToolsThisYear'),
                value: [moment().startOf('year'), moment().endOf('year')],
            },
            {
                key: 'y-1',
                text: intl.get('Report.ToolsLastYear'),
                value: [
                    moment()
                        .subtract(currentIndex === 5 ? lastIndex : 1, 'years')
                        .startOf('year'),
                    moment()
                        .subtract(currentIndex === 5 ? lastIndex : 1, 'years')
                        .endOf('year'),
                ],
            },
            // 追加业务自定义快捷按钮(如"近一个月"),key 不会被 disabledRanges 命中,始终展示
            ...extraRanges,
        ];
        return (
            disabledRanges.length ? defaultRanges.filter(({ key }) => !disabledRanges.includes(key)) : defaultRanges
        ).reduce(
            (s: Object, { text, value }: any) => ({
                ...s,
                [text]: value,
            }),
            {},
        ) as any;
    }

    constructor(props: iProps) {
        super(props);
        // 配置国际化语言(moment的locale未正确设置会导致部分语言错乱)
        moment.locale(languages.getLocale());
    }

    // 点击日期快捷选择不主动关闭面板
    private timer: any = null;
    timerHandle = () => {
        this.timer = setTimeout(() => {
            clearTimeout(this.timer);
            this.timer = null;
        }, 300);
    };

    // "昨天,上月,上一年"都可持续点击,每点击一次,自动往前跳1
    keepClick = (dates: any[]) => {
        if (!dates) return;
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        // "全部"等空区间快捷项:两端都为空时立即关闭面板(空选择 AntD 会禁用"确定",停留会让人以为卡住)
        if (!dates[0] && !dates[1]) {
            this.setState({ open: false, currentIndex: -1, currentIndex2: -1 });
            return;
        }
        let index = Object.values(this.ranges).findIndex((item) => JSON.stringify(item) === JSON.stringify(dates));
        let { currentIndex, lastIndex } = this.state;

        if (index !== -1) {
            // 昨天,上月,上一年 对应的索引值
            // setState 刷新原因，默认赋值为2
            if ([1, 3, 5].includes(index)) {
                lastIndex = currentIndex !== index ? 2 : lastIndex + 1;
            }
            this.timerHandle();
            currentIndex = index;
        } else if (!dates[0] || !dates[1]) {
            this.timer = 'no';
        }
        this.setState({ lastIndex, currentIndex, currentIndex2: index });
    };

    // 日期格式处理
    formatValue = (dateStrings: any[]): any => {
        if (Array.isArray(dateStrings) && dateStrings.length > 0)
            return [moment(dateStrings[0]), moment(dateStrings[1])];
        return null;
    };

    // 获取不可选时间（今天之后）
    disabledDate = (current: any) => {
        return current && current.valueOf() > Date.now();
    };

    // 获取不可选时间（今天及今天之后）
    disabledDateWithToday = (current: any) => {
        const currDate = current.format('YYYY-MM-DD');
        const today = moment(new Date()).format('YYYY-MM-DD');
        return current && (current.valueOf() > Date.now() || currDate == today);
    };

    // 禁止日期更改
    disabledDateRange = (current: any) => {
        const { disabledDateRange } = this.props;
        return current ? current.valueOf() < disabledDateRange[0] || current.valueOf() > disabledDateRange[1] : false;
    };

    // 时间选择更改事件
    onCalendarChange = (dates: any[]) => {
        const { onCalendarChange } = this.props;
        onCalendarChange && onCalendarChange(dates);
    };

    render() {
        const {
            defaultDate = [],
            disabled = false,
            showTime,
            disabledDate,
            bodyStyle,
            dropdownClassName,
            placeholder,
            forbidClear,
            disabledDateRange,
            onDateChange,
        } = this.props;
        return (
            <RangePicker
                allowEmpty={[true, true]}
                allowClear={!forbidClear}
                placeholder={placeholder}
                className="ant-date-picker h-[32px]"
                showTime={
                    showTime &&
                    ({
                        format: 'HH:mm:ss',
                        defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('23:59:59', 'HH:mm:ss')],
                    } as any)
                }
                disabledDate={
                    disabledDateRange
                        ? this.disabledDateRange
                        : disabledDate && (disabledDate == 'withToday' ? this.disabledDateWithToday : this.disabledDate)
                }
                format={showTime && 'YYYY-MM-DD HH:mm:ss'}
                ranges={this.ranges}
                style={bodyStyle}
                dropdownClassName={dropdownClassName}
                defaultValue={this.formatValue(defaultDate)}
                onChange={(dates: any[], dateStrings: any[]) => {
                    onDateChange?.(dateStrings);
                    this.onCalendarChange(dates);
                    this.keepClick(dates);
                }}
                onCalendarChange={(dates) => dates && this.onCalendarChange(dates)}
                onOk={(dates) => {
                    if (this.timer !== 'no' && (this.timer || this.state.currentIndex2 !== -1)) {
                        this.setState({ open: false });
                    }
                }}
                open={this.state.open}
                onOpenChange={(status) => {
                    this.setState({ open: status ? status : !!this.timer });
                }}
                disabled={disabled}
            />
        );
    }
}
